import { defineStore } from "pinia";
import { ref } from "vue";
import { PaymentMethod } from "@/helpers/interfaces/organisations";

/**
 * Payments store to manage payment-related data such as transaction ID,
 * available payment methods, selected provider, and provider details.
 * The store persists data in sessionStorage to maintain state across page
 * reloads within the same session.
 */
export const usePaymentsStore = defineStore(
    "payments",
    () => {
        // ── State ──────────────────────────────────────────────────────────────
        const paymentId = ref<string | null>(null);
        const paymentMethods = ref<PaymentMethod[] | null>(null);
        const selectedProvider = ref<string | null>(null);
        const selectedProviderDetails = ref<PaymentMethod | null>(null);
        const loadingPaymentMethods = ref(false);
        const isPaymentInProgress = ref(false);

        // ── Actions ────────────────────────────────────────────────────────────
        function setPaymentId(id: string | null) {
            /**
             * Sets the transaction id
             */
            paymentId.value = id;
        }

        function setPaymentMethods(methods: PaymentMethod[] | null) {
            /**
             * Sets the list of allowed payment methods in the organisation's base currency
             */
            paymentMethods.value = methods;
        }

        function setSelectedProvider(provider: string | null) {
            /**
             * Sets the provider to a given value to ensure that session reload
             * takes into account the user's selected payment method
             */
            selectedProvider.value = provider;
        }

        function setSelectedProviderDetails(details: PaymentMethod | null) {
            /**
             * Sets the provider information to a given value
             */
            selectedProviderDetails.value = details;
        }

        function setIsPaymentInProgress(value: boolean) {
            isPaymentInProgress.value = value;
        }

        return {
            paymentId,
            paymentMethods,
            selectedProvider,
            selectedProviderDetails,
            loadingPaymentMethods,
            isPaymentInProgress,
            setPaymentId,
            setPaymentMethods,
            setSelectedProvider,
            setSelectedProviderDetails,
            setIsPaymentInProgress,
        };
    },
    {
        persist: {
            pick: [
                "paymentId",
                "paymentMethods",
                "selectedProvider",
                "selectedProviderDetails",
            ],
            storage: sessionStorage,
        },
    },
);
